import { useState } from "react";
import {
  CheckCircle2,
  XCircle,
  MinusCircle,
  ChevronDown,
  ChevronRight,
  FlaskConical,
} from "lucide-react";

interface TestCase {
  name: string;
  status: "passed" | "failed" | "skipped";
  duration?: number;
  error?: string;
  suite?: string;
}

interface TestResultsBlockProps {
  passed: number;
  failed: number;
  skipped: number;
  tests: TestCase[];
  duration?: number;
}

const STATUS_CONFIG = {
  passed: { icon: CheckCircle2, color: "text-green-500", bg: "bg-green-500/10", label: "Passed" },
  failed: { icon: XCircle, color: "text-red-500", bg: "bg-red-500/10", label: "Failed" },
  skipped: { icon: MinusCircle, color: "text-yellow-500", bg: "bg-yellow-500/10", label: "Skipped" },
};

function TestCaseRow({ test }: { test: TestCase }) {
  const [expanded, setExpanded] = useState(test.status === "failed");
  const config = STATUS_CONFIG[test.status];
  const StatusIcon = config.icon;
  const hasDetails = !!test.error;

  return (
    <div className="border-b border-border/50 last:border-0">
      <button
        type="button"
        className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-accent/50 ${
          hasDetails ? "cursor-pointer" : "cursor-default"
        }`}
        onClick={() => hasDetails && setExpanded((p) => !p)}
        tabIndex={hasDetails ? 0 : -1}
      >
        <StatusIcon className={`h-3.5 w-3.5 shrink-0 ${config.color}`} />
        <span className="min-w-0 flex-1">
          {test.suite && (
            <span className="text-muted-foreground">{test.suite} &gt; </span>
          )}
          <span className={test.status === "failed" ? "font-medium" : ""}>{test.name}</span>
        </span>
        {test.duration !== undefined && (
          <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
            {test.duration}ms
          </span>
        )}
        {hasDetails && (
          expanded ? (
            <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
          )
        )}
      </button>
      {expanded && test.error && (
        <div className="mx-3 mb-2 rounded bg-red-500/5 p-2">
          <pre className="whitespace-pre-wrap font-mono text-[11px] text-red-600 dark:text-red-400">
            {test.error}
          </pre>
        </div>
      )}
    </div>
  );
}

export function TestResultsBlock({ passed, failed, skipped, tests, duration }: TestResultsBlockProps) {
  const [filter, setFilter] = useState<"all" | "passed" | "failed" | "skipped">("all");
  const total = passed + failed + skipped;
  const allPassing = failed === 0;

  const filteredTests = filter === "all" ? tests : tests.filter((t) => t.status === filter);

  return (
    <div className="my-2 overflow-hidden rounded-lg border border-border">
      {/* Summary header */}
      <div
        className={`flex items-center gap-2 border-b px-3 py-2 ${
          allPassing
            ? "bg-green-500/10 border-green-500/20"
            : "bg-red-500/10 border-red-500/20"
        }`}
      >
        <FlaskConical className={`h-4 w-4 ${allPassing ? "text-green-500" : "text-red-500"}`} />
        <span className="text-sm font-medium">
          {allPassing ? "All tests passed" : `${failed} test${failed > 1 ? "s" : ""} failed`}
        </span>
        <span className="ml-auto text-xs text-muted-foreground">
          {total} test{total !== 1 ? "s" : ""}
          {duration !== undefined && ` in ${(duration / 1000).toFixed(1)}s`}
        </span>
      </div>

      {/* Stats bar */}
      <div className="flex items-center gap-3 border-b bg-muted/30 px-3 py-1.5">
        {([
          ["all", total, "text-foreground"] as const,
          ["passed", passed, "text-green-500"] as const,
          ["failed", failed, "text-red-500"] as const,
          ["skipped", skipped, "text-yellow-500"] as const,
        ]).map(([key, count, color]) => (
          <button
            key={key}
            type="button"
            className={`text-xs font-medium transition-colors ${
              filter === key ? color : "text-muted-foreground hover:text-foreground"
            } ${count === 0 && key !== "all" ? "opacity-50" : ""}`}
            onClick={() => setFilter(key)}
            disabled={count === 0 && key !== "all"}
          >
            {key === "all" ? `All (${count})` : `${key.charAt(0).toUpperCase() + key.slice(1)} (${count})`}
          </button>
        ))}
      </div>

      {/* Progress bar */}
      {total > 0 && (
        <div className="flex h-1">
          {passed > 0 && (
            <div className="bg-green-500" style={{ width: `${(passed / total) * 100}%` }} />
          )}
          {failed > 0 && (
            <div className="bg-red-500" style={{ width: `${(failed / total) * 100}%` }} />
          )}
          {skipped > 0 && (
            <div className="bg-yellow-500" style={{ width: `${(skipped / total) * 100}%` }} />
          )}
        </div>
      )}

      {/* Test list */}
      <div className="max-h-[300px] overflow-auto">
        {filteredTests.map((test, i) => (
          <TestCaseRow key={`${test.name}-${i}`} test={test} />
        ))}
        {filteredTests.length === 0 && (
          <div className="p-3 text-center text-xs text-muted-foreground">
            No tests match the selected filter
          </div>
        )}
      </div>
    </div>
  );
}
